<template>
  <div class="nav-wrapper">
    <DismissLayer :visible="isMenuOpen" :z-index="340" @dismiss="closeMenu" />
    <div class="sidenav" :class="{ loading: isLoading, 'menu-open': isMenuOpen }">
      <div class="wrapper">
        <div class="new-logo">
          <img src="/assets/images/jidoka.svg" width="24" height="28" alt="" />
        </div>
        <div class="logo">
          <a href="javascript:void(0)">Jidoka</a>
          <button type="button" class="nav-icon" :class="{ open: isMenuOpen }" aria-label="Toggle navigation"
            :aria-expanded="isMenuOpen ? 'true' : 'false'"
            @click="toggleMenu"><span></span><span></span><span></span><span></span></button>
        </div>
        <div class="menu" :class="{ open: isMenuOpen }">
          <ul class="nav">
            <!-- User Info -->
            <li class="user-info" :class="{ 'loading-placeholder': isLoading }">
              <a href="javascript:void(0)" ref="userAvatar">
                <img :src="userAvatarUrl" height="32" width="32"
                  :alt="isLoading ? 'Loading user avatar' : 'User avatar'" />
                <span>{{ displayName }}</span>
              </a>
            </li>

            <li v-if="updateAvailable" class="nav-update-available nav-update-available--mobile">
              <button type="button" class="nav-update-available__button"
                aria-label="New version of Jidoka available. Refresh now." @click="refreshApplication">
                <span class="icon-update-21 nav-update-available__icon" aria-hidden="true"></span>
                <span>Update available</span>
              </button>
            </li>

            <li v-if="user?.uid" class="nav-notifications">
              <NotificationPanel :uid="user.uid" />
            </li>

            <!-- Navigation Links -->
            <li v-for="(link, key) in navLinks" :key="key"
              :class="{ 'nav-link': true, disabled: key === currentPage }">
              <a :href="key === currentPage ? 'javascript:void(0)' : `${baseURL}${key}`" @click="closeMenu">
                <i class="material-icons" :data-tippy-content="key !== currentPage ? link.name : undefined">
                  {{ link.icon }}
                </i>
                <span>{{ link.name }}</span>
              </a>
            </li>
          </ul>
        </div>
        <div v-if="updateAvailable" class="nav-update-available nav-update-available--desktop">
          <button type="button" class="nav-update-available__button"
            data-tippy-content="New version of Jidoka available"
            aria-label="New version of Jidoka available. Refresh now." @click="refreshApplication">
            <span class="icon-update-21 nav-update-available__icon" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { logOut, switchAccount, getCurrentUser, CachedUser } from '../../modules/firebase/Auth';
import { UserStore } from '../../modules/users/UserStore';
import { getThemeMode, setThemeMode, subscribeTheme, ThemeMode } from '../../modules/ui/theme';
import tippy, { roundArrow } from 'tippy.js';
import { UPDATE_AVAILABLE_EVENT, versionManager } from '../../modules/updates/versionManager';
import NotificationPanel from '../notifications/NotificationPanel.vue';
import { DismissLayer } from '../ui';

interface NavLink {
  name: string;
  icon: string;
}

// Max time to wait for user data before redirecting to login
const USER_DATA_TIMEOUT_MS = 5000;
// Retry interval for checking user data
const USER_DATA_RETRY_MS = 250;
// Avatar cache key in localStorage
const AVATAR_CACHE_KEY = 'JIDOKA_USER_AVATAR_CACHE';

export default defineComponent({
  name: 'Nav',
  components: { DismissLayer, NotificationPanel },
  setup() {
    const user = ref<CachedUser | null>(null);
    const userAvatarUrl = ref('/assets/img/avatar.png');
    const currentPage = ref('');
    const userAvatar = ref<HTMLElement | null>(null);
    const isLoading = ref(true);
    const isMenuOpen = ref(false);
    const updateAvailable = ref(Boolean(versionManager.availableBuildId && versionManager.availableBuildId !== versionManager.currentBuildId));

    // Retry/timeout state
    let retryInterval: ReturnType<typeof setInterval> | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let avatarCheckInterval: ReturnType<typeof setInterval> | null = null;
    let tooltipInstances: Array<{ destroy: () => void }> = [];
    let unsubscribeTheme: (() => void) | null = null;
    const themeMode = ref<ThemeMode>(getThemeMode());

    // Computed display name with fallback
    const displayName = computed(() => user.value?.displayName || (isLoading.value ? 'Loading...' : 'User'));

    const navLinks: Record<string, NavLink> = {
      ncr: { name: 'Non-Conformance Report', icon: 'report' },
      // issue: { name: 'Issues', icon: 'call_merge' },
      // label: { name: 'Storage Label', icon: 'label' },
      labelhub: { name: 'LabelHub', icon: 'local_printshop' },
      shipments: { name: 'Shipment Tracking', icon: 'local_shipping' },
    };

    const baseURL = `${window.location.protocol}//${window.location.host}/${window.location.pathname.split('/')[0]}`;

    const getCurrentPageKey = () => window.location.pathname.replace(/^\/+/, '').split('/')[0] || '';

    /**
     * Get cached avatar URL from localStorage
     */
    const getCachedAvatar = (email: string): string | null => {
      try {
        const cached = localStorage.getItem(AVATAR_CACHE_KEY);
        if (cached) {
          const data = JSON.parse(cached);
          if (data.email === email && data.url) {
            return data.url;
          }
        }
      } catch (error) {
        console.warn('[Nav] Error reading avatar cache:', error);
      }
      return null;
    };

    /**
     * Save avatar URL to localStorage for instant loading on next page
     */
    const cacheAvatar = (email: string, url: string): void => {
      try {
        localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify({
          email,
          url,
          timestamp: Date.now()
        }));
      } catch (error) {
        console.warn('[Nav] Error caching avatar:', error);
      }
    };

    const registerTooltipInstances = (instances: { destroy: () => void } | Array<{ destroy: () => void }>) => {
      tooltipInstances.push(...(Array.isArray(instances) ? instances : [instances]));
    };

    const destroyTooltips = () => {
      tooltipInstances.forEach((instance) => {
        instance.destroy();
      });
      tooltipInstances = [];
    };

    const getThemeButtonState = (mode: ThemeMode) => themeMode.value === mode ? 'true' : 'false';

    const renderUserMenuContent = () => `
      <div class="nav-user-menu">
        <div class="nav-user-menu__section">
          <div class="nav-user-menu__label">Theme</div>
          <div class="nav-user-menu__theme-options" role="group" aria-label="Theme preference">
            <button type="button" class="nav-user-menu__theme-option" data-theme-mode="system" aria-pressed="${getThemeButtonState('system')}">System</button>
            <button type="button" class="nav-user-menu__theme-option" data-theme-mode="light" aria-pressed="${getThemeButtonState('light')}">Light</button>
            <button type="button" class="nav-user-menu__theme-option" data-theme-mode="dark" aria-pressed="${getThemeButtonState('dark')}">Dark</button>
          </div>
        </div>
        <div class="nav-user-menu__section nav-user-menu__section--actions">
          <a class="nav-user-menu__action" href="/settings">Settings</a>
          <button type="button" class="nav-user-menu__action" id="switchAccountButton">Switch Account</button>
          <button type="button" class="nav-user-menu__action" id="logoutButton">Log Out</button>
        </div>
      </div>
    `;

    const syncUserMenuThemeState = () => {
      document.querySelectorAll<HTMLButtonElement>('.nav-user-menu__theme-option[data-theme-mode]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.themeMode === themeMode.value ? 'true' : 'false');
      });
    };

    const getThemeModeFromButton = (button: HTMLButtonElement): ThemeMode | null => {
      const mode = button.dataset.themeMode;
      return mode === 'system' || mode === 'light' || mode === 'dark' ? mode : null;
    };

    const initTooltips = () => {
      destroyTooltips();

      // Initialize tooltips for nav icons (desktop only)
      if (window.innerWidth > 780) {
        registerTooltipInstances(tippy('.nav-wrapper [data-tippy-content]', {
          placement: 'right',
          arrow: false
        }));
      }

      // Initialize user avatar tooltip
      if (userAvatar.value) {
        registerTooltipInstances(tippy(userAvatar.value, {
          theme: 'user',
          content: renderUserMenuContent(),
          interactive: true,
          allowHTML: true,
          placement: window.innerWidth > 768 ? 'right-end' : 'bottom',
          offset: window.innerWidth > 768 ? [-52, 8] : [0, 8],
          trigger: 'click',
          triggerTarget: userAvatar.value.closest('.user-info') as HTMLElement,
          arrow: roundArrow,
          appendTo: document.body,
          onShow(instance) {
            instance.setContent(renderUserMenuContent());
          },
          onMount(instance) {
            const logoutBtn = instance.popper.querySelector<HTMLButtonElement>('#logoutButton');
            const switchAccountBtn = instance.popper.querySelector<HTMLButtonElement>('#switchAccountButton');
            if (logoutBtn) {
              logoutBtn.addEventListener('click', logOut);
            }
            if (switchAccountBtn) {
              switchAccountBtn.addEventListener('click', switchAccount);
            }

            instance.popper.querySelectorAll<HTMLButtonElement>('.nav-user-menu__theme-option[data-theme-mode]').forEach((button) => {
              button.addEventListener('click', () => {
                const mode = getThemeModeFromButton(button);
                if (mode) {
                  setThemeMode(mode);
                }
              });
            });

            syncUserMenuThemeState();
          },
          onHide(instance) {
            const logoutBtn = instance.popper.querySelector<HTMLButtonElement>('#logoutButton');
            const switchAccountBtn = instance.popper.querySelector<HTMLButtonElement>('#switchAccountButton');
            if (logoutBtn) {
              logoutBtn.removeEventListener('click', logOut);
            }
            if (switchAccountBtn) {
              switchAccountBtn.removeEventListener('click', switchAccount);
            }
          }
        }));
      }
    };

    const closeMenu = () => {
      isMenuOpen.value = false;
    };

    const toggleMenu = () => {
      isMenuOpen.value = !isMenuOpen.value;
    };

    const handleWindowResize = () => {
      if (window.innerWidth > 768) {
        closeMenu();
      }
    };

    const handleUpdateAvailable = () => { updateAvailable.value = true; };
    const refreshApplication = () => { void versionManager.refreshToLatest(); };

    /**
     * Try to load user data - called on mount and by retry interval
     * Returns true if user data was successfully loaded
     */
    const tryLoadUser = async (): Promise<boolean> => {
      const currentUser = getCurrentUser();

      if (!currentUser) {
        return false;
      }

      user.value = currentUser;

      // Try cached avatar first for instant load
      const cachedAvatar = getCachedAvatar(currentUser.email);

      // Get Slack avatar from UserStore (only if initialized)
      const slackAvatar = UserStore.isInitialized() ? UserStore.getAvatar(currentUser.email) : null;

      // Priority: cached avatar (instant) > Slack avatar > Google photo > default
      userAvatarUrl.value = cachedAvatar || slackAvatar || currentUser.photoURL || '/assets/img/avatar.png';

      // If we got Slack avatar, update cache
      if (slackAvatar) {
        cacheAvatar(currentUser.email, slackAvatar);
      }

      // Get current page
      currentPage.value = getCurrentPageKey();

      // Mark as loaded and cleanup retry handles
      isLoading.value = false;
      cleanupRetryHandles();

      // If UserStore wasn't ready yet, set up a listener to update avatar when Slack data becomes available
      // This handles the case where we're showing cached/Google photo but need to verify against latest Slack avatar
      if (!slackAvatar) {
        checkForUserStoreAvatar();
      }

      return true;
    };

    /**
     * Check periodically if UserStore has loaded and update avatar
     * This handles the case where Nav loads before UserStore is initialized
     */
    const cleanupAvatarCheck = () => {
      if (avatarCheckInterval) {
        clearInterval(avatarCheckInterval);
        avatarCheckInterval = null;
      }
    };

    const checkForUserStoreAvatar = () => {
      cleanupAvatarCheck();

      const maxAttempts = 20; // 5 seconds max (250ms * 20)
      let attempts = 0;

      avatarCheckInterval = setInterval(() => {
        attempts++;

        if (UserStore.isInitialized() && user.value) {
          const slackAvatar = UserStore.getAvatar(user.value.email);
          if (slackAvatar) {
            userAvatarUrl.value = slackAvatar;
            // Cache the newly loaded Slack avatar
            cacheAvatar(user.value.email, slackAvatar);
            cleanupAvatarCheck();
          }
        }

        // Stop checking after max attempts
        if (attempts >= maxAttempts) {
          cleanupAvatarCheck();
        }
      }, 250);
    };

    /**
     * Cleanup retry interval and timeout handles
     */
    const cleanupRetryHandles = () => {
      if (retryInterval) {
        clearInterval(retryInterval);
        retryInterval = null;
      }
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
    };

    /**
     * Redirect to login page, preserving current path for return
     */
    const redirectToLogin = () => {
      cleanupRetryHandles();
      console.warn('[Nav] User data timeout, redirecting to login');
      const targetPath = window.location.pathname.replace(/^\/+/, '');
      const path = targetPath.length >= 3 ? '/?to=' : '/';
      window.location.assign(path + targetPath);
    };

    watch(updateAvailable, async (available) => {
      if (!available) {
        return;
      }

      await nextTick();
      initTooltips();
    });

    onMounted(async () => {
      currentPage.value = getCurrentPageKey();

      await nextTick();
      initTooltips();
      window.addEventListener('resize', handleWindowResize);
      window.addEventListener(UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
      unsubscribeTheme = subscribeTheme(({ mode }) => {
        themeMode.value = mode;
        syncUserMenuThemeState();
      });

      // Try to load user immediately
      const loaded = await tryLoadUser();

      if (!loaded) {
        // User data not available yet - start retry loop
        console.warn('[Nav] User data not available, retrying...');

        // Set timeout to redirect if user data never arrives
        timeoutHandle = setTimeout(() => {
          if (isLoading.value) {
            redirectToLogin();
          }
        }, USER_DATA_TIMEOUT_MS);

        // Retry periodically (auth may be populating USER_KEY)
        retryInterval = setInterval(async () => {
          const success = await tryLoadUser();
          if (success) {
            console.log('[Nav] User data loaded after retry');
          }
        }, USER_DATA_RETRY_MS);
      }
    });

    // Cleanup on unmount
    onUnmounted(() => {
      cleanupRetryHandles();
      cleanupAvatarCheck();
      if (unsubscribeTheme) {
        unsubscribeTheme();
        unsubscribeTheme = null;
      }
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener(UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
      destroyTooltips();
    });

    return {
      user,
      userAvatarUrl,
      displayName,
      currentPage,
      navLinks,
      baseURL,
      userAvatar,
      isLoading,
      isMenuOpen,
      toggleMenu,
      closeMenu,
      updateAvailable,
      refreshApplication
    };
  }
});
</script>
